import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getCorrectionApprovalSummary,
  getMyCorrectionApprovals,
  decideCorrectionApprovalStep,
} from "../../../api/projectApprovalApi";
import { UserContext } from "../../../contexts/CurrentUserContext";
import Estimate from "../../ProjectsV2/ProjectFinance/Estimate/Estimate";
import Preloader from "../../Preloader/Preloader";
import { formatCorrectionApprovalAmount } from "./projectApprovalHelpers";
import "./ProjectApprovalDetail.css";

const LIST_PATH = "/approvals/project-approval?tab=corrections";

const STEP_STATUS_LABELS = {
  approved: "Согласован",
  pending: "На рассмотрении",
  rejected: "Отклонён",
};

const isCancelledStatus = (status) =>
  String(status || "")
    .toLowerCase()
    .includes("отменен");

const isPendingStepStatus = (status) => {
  const normalized = String(status || "").toLowerCase();
  return (
    normalized === "pending" ||
    normalized === "на рассмотрении" ||
    normalized === "" ||
    status == null
  );
};

const getStepStatusLabel = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "approved" || normalized === "согласован") {
    return STEP_STATUS_LABELS.approved;
  }
  if (
    normalized === "rejected" ||
    normalized === "не согласован" ||
    normalized === "отклонен" ||
    normalized === "отклонён"
  ) {
    return STEP_STATUS_LABELS.rejected;
  }
  if (isCancelledStatus(status)) {
    return "Отменен инициатором";
  }
  if (isPendingStepStatus(status)) {
    return STEP_STATUS_LABELS.pending;
  }
  return status || "—";
};

const getStepStatusIconGlyph = (kind) => {
  if (kind === "approved") return "✓";
  if (kind === "rejected" || kind === "cancelled") return "✕";
  return "⏳";
};

const getStepStatusIconClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "approved" || normalized === "согласован") {
    return "approved";
  }
  if (
    normalized === "rejected" ||
    normalized === "не согласован" ||
    normalized === "отклонен" ||
    normalized === "отклонён"
  ) {
    return "rejected";
  }
  if (isCancelledStatus(status)) {
    return "cancelled";
  }
  return "pending";
};

function CorrectionApprovalDetail() {
  const { projectId, approvalId } = useParams();
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const [approval, setApproval] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [rejectComment, setRejectComment] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);

  const fetchApproval = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      let foundApproval = null;
      try {
        foundApproval = await getCorrectionApprovalSummary(parseInt(approvalId));
      } catch (summaryError) {
        const approvals = await getMyCorrectionApprovals({
          projectId: parseInt(projectId),
        });
        foundApproval = (approvals || []).find(
          (item) => Number(item?.id) === parseInt(approvalId),
        );
        if (!foundApproval) throw summaryError;
      }

      if (!foundApproval) {
        setError("Согласование корректировок не найдено");
        return;
      }

      setApproval(foundApproval);
    } catch (err) {
      setError("Ошибка при загрузке данных согласования корректировок");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, approvalId]);

  useEffect(() => {
    fetchApproval();
  }, [fetchApproval]);

  const steps = Array.isArray(approval?.steps) ? approval.steps : [];
  const myStep = steps.find(
    (step) =>
      userData?.id != null &&
      String(step.approver_id) === String(userData.id),
  );
  const canDecide =
    myStep &&
    isPendingStepStatus(myStep.status) &&
    !isCancelledStatus(approval?.status);

  const headerStatusText = isCancelledStatus(approval?.status)
    ? "Отменен инициатором"
    : canDecide
      ? "На рассмотрении"
      : myStep
        ? getStepStatusLabel(myStep.status)
        : "Согласование корректировок";

  const handleApprove = async () => {
    if (!canDecide || !myStep?.id || !projectId) return;

    try {
      setIsProcessing(true);
      setError(null);
      await decideCorrectionApprovalStep(parseInt(projectId), myStep.id, {
        status: "согласован",
      });
      navigate(LIST_PATH);
    } catch (err) {
      setError(err?.message || "Ошибка при согласовании корректировок");
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!canDecide || !myStep?.id || !projectId) return;
    if (!rejectComment.trim()) return;

    try {
      setIsProcessing(true);
      setError(null);
      await decideCorrectionApprovalStep(parseInt(projectId), myStep.id, {
        status: "не согласован",
        comment: rejectComment.trim(),
      });
      navigate(LIST_PATH);
    } catch (err) {
      setError(err?.message || "Ошибка при отклонении корректировок");
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return <Preloader />;
  }

  if (error && !approval) {
    return (
      <div className="project-approval-detail">
        <div className="project-approval-detail__error">{error}</div>
        <button
          className="project-approval-detail__back-button"
          onClick={() => navigate(LIST_PATH)}
        >
          Вернуться к списку
        </button>
      </div>
    );
  }

  const amountLabel = formatCorrectionApprovalAmount(approval?.total_price);

  return (
    <div className="project-approval-detail">
      <div className="project-approval-detail__status-header">
        <button
          className="project-approval-detail__back-button-header"
          onClick={() => navigate(LIST_PATH)}
        >
          <span className="project-approval-detail__back-arrow">←</span>
          <span className="project-approval-detail__back-text">
            Назад к списку корректировок
          </span>
        </button>
        <div className="project-approval-detail__status-content">
          <span className="project-approval-detail__status-icon">📄</span>
          <span className="project-approval-detail__status-text">
            {headerStatusText}
          </span>
        </div>
      </div>

      <div className="project-approval-detail__comment-block">
        <div className="project-approval-detail__comment-label">
          Сумма заказчику
        </div>
        <div className="project-approval-detail__comment-text">
          {amountLabel}
        </div>
      </div>

      {approval?.comment?.trim() && (
        <div className="project-approval-detail__comment-block">
          <div className="project-approval-detail__comment-label">
            Комментарий
          </div>
          <div className="project-approval-detail__comment-text">
            {approval.comment}
          </div>
        </div>
      )}

      {steps.length > 0 && (
        <div className="project-approval-detail__approval-status-section">
          <div className="project-approval-detail__approval-status-block">
            <div className="project-approval-detail__approval-status-title">
              Шаги согласования
            </div>
            <div className="project-approval-detail__approval-steps">
              {steps.map((step, index) => {
                const displayStatus = isCancelledStatus(approval?.status)
                  ? approval.status
                  : step.status;
                const iconKind = getStepStatusIconClass(displayStatus);
                return (
                  <div
                    key={step.id || index}
                    className="project-approval-detail__approval-step"
                  >
                    <span className="project-approval-detail__approver-name">
                      {step.approver_name || "Не указан"}:
                    </span>
                    <span className={`approval-status-icon ${iconKind}`}>
                      {getStepStatusIconGlyph(iconKind)}
                    </span>
                    <span className="project-approval-detail__step-status-text">
                      {getStepStatusLabel(displayStatus)}
                    </span>
                    {step.comment?.trim() && (
                      <span className="project-approval-detail__step-comment">
                        ({step.comment})
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="project-approval-detail__estimate-container">
        <Estimate projectId={projectId} isReadOnly={true} />
      </div>

      {canDecide && (
        <div className="project-approval-detail__actions">
          {!showRejectForm ? (
            <>
              <button
                type="button"
                className="project-approval-detail__button project-approval-detail__button--reject"
                onClick={() => setShowRejectForm(true)}
                disabled={isProcessing}
              >
                Отклонить
              </button>
              <button
                type="button"
                className="project-approval-detail__button project-approval-detail__button--approve"
                onClick={handleApprove}
                disabled={isProcessing}
              >
                {isProcessing ? "Обработка..." : "Согласовать"}
              </button>
            </>
          ) : (
            <div className="project-approval-detail__reject-form">
              <label className="project-approval-detail__reject-label">
                Комментарий к отклонению
              </label>
              <textarea
                className="project-approval-detail__textarea"
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                rows={3}
                placeholder="Укажите причину отклонения"
                disabled={isProcessing}
              />
              <div className="project-approval-detail__reject-actions">
                <button
                  type="button"
                  className="project-approval-detail__button project-approval-detail__button--secondary"
                  onClick={() => {
                    setShowRejectForm(false);
                    setRejectComment("");
                  }}
                  disabled={isProcessing}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="project-approval-detail__button project-approval-detail__button--reject"
                  onClick={handleReject}
                  disabled={isProcessing || !rejectComment.trim()}
                >
                  {isProcessing ? "Отклонение..." : "Подтвердить отклонение"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="project-approval-detail__error-message" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

export default CorrectionApprovalDetail;
